<template>
  <div class="highcharts-light pie-chart chart-container">
    <highcharts :options="chartOptions" ref="chartComponent"></highcharts>
  </div>
</template>

<script>
import {Chart} from 'highcharts-vue';
import Highcharts from 'highcharts';
import addAccessibility from 'highcharts/modules/accessibility';
import noDataToDisplay from 'highcharts/modules/no-data-to-display';
import DataService from '../../../../services/data.service';

addAccessibility(Highcharts);
noDataToDisplay(Highcharts);

function formatSmallNumber(percentage, digits) {
  let v = percentage;
  while (percentage * 10**digits < 1 && digits < 6) {
    digits++;
  }
  if (digits === 6) { digits = 0; }
  return parseFloat(percentage).toFixed(digits);
}

export default {
  name: 'pie-chart',
  components: {
    highcharts: Chart
  },
  props: {
    chartSeries: {
      type: Array,
      default: () => []
    },
    dateRange: {
      type: Object,
      default: undefined,
    },
    filter: {
      type: Object,
      default: undefined
    },
    panel: {
      type: Object,
      default: () => {}
    },
  },
  data: function () {
    this.filter.activeFiltersUpdated.add(this.renewRegistration.bind(this));
    return {
      data: [],
    }
  },
  computed: {
    chartOptions: function() {
      let self = this;
      return {
        chart: {
          styledMode: true,
          type: 'pie',
          height: 315
        },
        title: {
          text: undefined
        },
        lang: {
          noData: 'No Data To Display'
        },
        noData: {
          style: {
            fontSize: '14px',
            fontWeight: '400',
            color: 'var(--text-color)',
            fontFamily: 'Proxima Nova'
          }
        },
        legend: {
          floating: true,
          align: 'left',
          layout: 'vertical',
          verticalAlign: 'bottom',
          borderRadius: 12,
          itemMarginBottom: 6,
          symbolHeight: 15,
          useHTML: true,
          itemStyle: {
            fontSize: '14px',
            color: 'var(--text-color)',
            fontFamily: 'Proxima Nova'
          },
          labelFormatter: function() {
            // todo: this case-by-case strategy for alignment really sucks, but I'm not sure if highcharts lets us customize the legend as a whole rather than each individual line,
            //       so the desired table-style layout may not be accomplishable in a reasonable way.
            //       Should look into modifying the CSS of the highcharts-generated legend HTML; may need to use !important styles to override
            let lcName = this.name.toLowerCase(),
                margin = 10;
            if (lcName === 'fair' || lcName === 'bad') {
              margin = 20;
            } else if (lcName === 'wifi') {
              margin = 39;
            } else if (lcName === 'cellular') {
              margin = 15;
            } else if (lcName === 'vpn') {
              margin = 35;
            }
            let number = this.percentage >= 1 ? Math.round(this.percentage) : formatSmallNumber(this.percentage, 1);
            return '<div style="position: relative; bottom: 1px;">' +
                     '<span style="margin-right: ' + margin + 'px; font-weight: normal">' + this.name + '</span><span style="font-weight: bold">' + number + '%</span>' +
                   '</div>';
          }
        },
        tooltip: {
          style: {
            fontFamily: 'Proxima Nova'
          },
          useHTML: true,
          formatter: function() {
            return `<svg width="10" height="10"><circle cx="5" cy="5" r="4"/></svg> ` + this.point.name + ': <b>' + parseFloat(this.percentage.toFixed(2)) + '%</b>' +
                   '<br>' +
                   '<span style="position:relative;left:10px;">(' + this.y + ' total)</span>';
          }
        },
        plotOptions: {
          series: {
            point: {
              events: {
                click: function (event) {
                  self.$emit('clickCallback', self.panel.groupBy, event.point.options.name);
                },
                mouseOver: function () {
                  if (this.series.chart.plotHeight + this.series.chart.legend.legendWidth > this.series.chart.chartWidth) {
                    this.series.chart.legend.update({ enabled: false });
                  }
                },
                mouseOut: function () {
                  this.series.chart.legend.update({ enabled: true });
                }
              }
            }
          },
          pie: {
            size: '100%',
            center:["50%", "50%"],
            cursor: 'pointer',
            dataLabels: {
              enabled: false
            },
            showInLegend: true
          }
        },
        credits: {
          enabled: false
        },
        series: [{
          name: 'Percentage',
          colorByPoint: true,
          data: self.data
        }],
        responsive: {
          rules: [{
            condition: {
              maxWidth: 700
            },
            chartOptions: {
              plotOptions: {
                pie: {
                center:["50%", "50%"],
                  }
              },
            }
          },
          {
            condition: {
              maxWidth: 625
            },
            chartOptions: {
              plotOptions: {
                pie: {
                center:["50%", "50%"],
                  }
              },
            }
          },
          {
            condition: {
              maxWidth: 600
            },
            chartOptions: {
              plotOptions: {
                pie: {
                center:["52.5%", "50%"],
                  }
              },
            }
          },
          {
            condition: {
              maxWidth: 575
            },
            chartOptions: {
              plotOptions: {
                pie: {
                center:["55%", "50%"],
                  }
              },
            }
          },
          {
            condition: {
              maxWidth: 550
            },
            chartOptions: {
              plotOptions: {
                pie: {
                center:["57.5%", "50%"],
                  }
              },
            }
          },
          {
            condition: {
              maxWidth: 525
            },
            chartOptions: {
              plotOptions: {
                pie: {
                  center: ["60%", "50%"],
                }
              },
            }
          },
          {
            condition: {
              maxWidth: 500
            },
            chartOptions: {
              plotOptions: {
                pie: {
                  // center: ["73.4%", "50%"],
                  center: ["62.5%", "50%"],
                }
              },
            }
          },
          {
            condition: {
              maxWidth: 450
            },
            chartOptions: {
              plotOptions: {
                pie: {
                  center: ["65%", "50%"],
                }
              },
            }
          },
          {
            condition: {
              maxWidth: 400
            },
            chartOptions: {
              plotOptions: {
                pie: {
                  center: ["60%", "50%"],
                },
              },
            }
          },
          {
            condition: {
              maxWidth: 350
            },
            chartOptions: {
              plotOptions: {
                pie: {
                  center: ["55%", "50%"],
                },
              },
            }
          },
          {
            condition: {
              maxWidth: 300
            },
            chartOptions: {
              plotOptions: {
                pie: {
                  center: ["50%", "50%"],
                }
              },
            }
          },
          {
            condition: {
              maxWidth: 290
            },
            chartOptions: {
              plotOptions: {
                pie: {
                  center: ["50%", "47.5%"],
                }
              },
            }
          },
          {
            condition: {
              maxWidth: 280
            },
            chartOptions: {
              plotOptions: {
                pie: {
                  center: ["50%", "45%"],
                }
              },
            }
          },
          {
            condition: {
              maxWidth: 270
            },
            chartOptions: {
              plotOptions: {
                pie: {
                  center: ["50%", "42.5%"],
                }
              },
            }
          },
          {
            condition: {
              maxWidth: 260
            },
            chartOptions: {
              plotOptions: {
                pie: {
                  center: ["50%", "40%"],
                }
              },
            }
          },
          {
            condition: {
              maxWidth: 250
            },
            chartOptions: {
              plotOptions: {
                pie: {
                  center: ["50%", "37.5%"],
                }
              },
            }
          },
          {
            condition: {
              maxWidth: 240
            },
            chartOptions: {
              plotOptions: {
                pie: {
                  center: ["50%", "35%"],
                }
              },
            }
          },
          {
            condition: {
              maxWidth: 230
            },
            chartOptions: {
              plotOptions: {
                pie: {
                  center: ["50%", "32.5%"],
                }
              },
            }
          },
          {
            condition: {
              maxWidth: 220
            },
            chartOptions: {
              plotOptions: {
                pie: {
                  center: ["50%", "30%"],
                }
              },
            }
          },
          {
            condition: {
              maxWidth: 210
            },
            chartOptions: {
              plotOptions: {
                pie: {
                  center: ["50%", "27.5%"],
                }
              },
            }
          },
          {
            condition: {
              maxWidth: 200
            },
            chartOptions: {
              plotOptions: {
                pie: {
                  center: ["50%", "25%"],
                }
              },
            }
          },
          {
            condition: {
              maxWidth: 190
            },
            chartOptions: {
              plotOptions: {
                pie: {
                  center: ["50%", "22.5%"],
                }
              },
            }
          }]
        }
      }
    }
  },
  methods: {
    renewRegistration: function() {
      DataService.deregisterPanel(this.panel, this.filter, this.dateRange);
      DataService.registerPanel(this.panel, this.filter, this.dateRange, this.updateData.bind(this));
    },
    updateData: function(data) {
      this.data = data;
      let self = this;
      if (self.panel.colors) {
        self.data.forEach(series => {
          if (series.name && self.panel.colors[series.name.toLowerCase()]) {
            series.colorIndex = self.panel.colors[series.name.toLowerCase()];
          }
        })
      }
      // We need to wait a bit to call reflow here; $nextTick seems to be the cleanest option.
      this.$nextTick(() => {
        if (self.$refs.chartComponent) {
          if (self.$refs.chartComponent.$el) {
            for (let i = 1; i < 10; i++) {
              self.$refs.chartComponent.$el.classList.remove('hc-series-' + i);
            }
            let valueLength = this.data.length;
            if (this.data && valueLength > 1) {
              self.$refs.chartComponent.$el.classList.add('hc-series-' + Math.min(10, valueLength));
            }
          }
          self.$refs.chartComponent.chart.reflow();
        }
      });

    }
  },
  mounted() {
    let self = this;
    // We need to wait a bit to call reflow here; $nextTick seems to be the cleanest option.
    this.$nextTick(() => { 
      self.$refs.chartComponent.chart.reflow();
    });
    DataService.registerPanel(this.panel, this.filter, this.dateRange, this.updateData);
  },
  beforeDestroy() {
    DataService.deregisterPanel(this.panel, this.filter, this.dateRange);
  },
  watch: {
    dateRange: {
      handler: function(newRange, oldVal) {
        if (!newRange.startDate.isSame(oldVal.startDate) || !newRange.endDate.isSame(oldVal.endDate)) {
          this.renewRegistration();
        }
      },
      deep: true,
    },
  },
}
</script>

<style lang="scss" scoped>
.pie-chart {}
</style>

<style lang="scss">
.pie-chart.chart-container {
  .highcharts-container {
    margin: 0 auto;
    .highcharts-legend-item span {
      font-size: 14px;
    }
    .highcharts-series {
      cursor: pointer;
    }
    .highcharts-legend-box {
      fill: var(--panel-background);
      fill-opacity: 0.80;
    }
  }
}
</style>
